import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { BadgeNotificationProvider } from './context/BadgeNotificationContext';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import ChildProfile from './pages/ChildProfile';
import AddChild from './pages/AddChild';
import AddMeasurement from './pages/AddMeasurement';
import AddAthleticTests from './pages/AddAthleticTests';
import UploadAthleticTests from './pages/UploadAthleticTests';
import PublicRegistration from './pages/PublicRegistration';
import GrowthCharts from './pages/GrowthCharts';
import AddCoachRating from './pages/AddCoachRating';
import SMHATAssessment from './pages/SMHATAssessment';
import CoachDashboard from './pages/CoachDashboard';
import AdminPanel from './pages/AdminPanel';
import Bookings from './pages/Bookings';
import ConsentForms from './pages/ConsentForms';
import ParentPortal from './pages/ParentPortal';
import AcceptInvitation from './pages/AcceptInvitation';
import AlertsPage from './pages/AlertsPage';
import GoogleSheetsSettings from './pages/GoogleSheetsSettings';
import NotificationSettings from './pages/NotificationSettings';
import ServicesInfo from './pages/ServicesInfo';
import Login from './pages/Login';
import BrandedLogin from './pages/BrandedLogin';
import ReferralPage from './pages/ReferralPage';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import OnboardingWizard from './components/OnboardingWizard';
import ParentRegistration from './components/ParentRegistration';
import ParentDashboard from './components/ParentDashboard';
import '@/App.css';

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <div className="App">
          <Toaster position="top-right" theme="light" />
          <AuthProvider>
            <BadgeNotificationProvider>
              <BrowserRouter>
              <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/login/:slug" element={<BrandedLogin />} />
              <Route path="/refer" element={<ReferralPage />} />
              <Route path="/refer/:slug" element={<ReferralPage />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/subscription-success" element={<SubscriptionSuccess />} />
              <Route path="/public-register" element={<PublicRegistration />} />
              <Route path="/parent-portal/:token" element={<ParentPortal />} />
              <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
              <Route path="/parent-registration" element={<ParentRegistration />} />
            
            {/* Onboarding */}
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <OnboardingWizard />
              </ProtectedRoute>
            } />
            
            {/* Parent Dashboard */}
            <Route path="/parent-dashboard" element={
              <ProtectedRoute allowedRoles={['parent']}>
                <ParentDashboard />
              </ProtectedRoute>
            } />
            
            {/* Protected routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/coach-dashboard" element={
              <ProtectedRoute allowedRoles={['coach', 'admin']}>
                <CoachDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminPanel />
              </ProtectedRoute>
            } />
            <Route path="/bookings" element={
              <ProtectedRoute allowedRoles={['coach', 'admin']}>
                <Bookings />
              </ProtectedRoute>
            } />
            <Route path="/consent-forms" element={
              <ProtectedRoute allowedRoles={['coach', 'parent', 'admin', 'partner']}>
                <ConsentForms />
              </ProtectedRoute>
            } />
            <Route path="/alerts" element={
              <ProtectedRoute allowedRoles={['coach', 'admin', 'partner']}>
                <AlertsPage />
              </ProtectedRoute>
            } />
            <Route path="/settings/google-sheets" element={
              <ProtectedRoute allowedRoles={['coach', 'admin']}>
                <GoogleSheetsSettings />
              </ProtectedRoute>
            } />
            <Route path="/settings/notifications" element={
              <ProtectedRoute>
                <NotificationSettings />
              </ProtectedRoute>
            } />
            <Route path="/services" element={
              <ProtectedRoute>
                <ServicesInfo />
              </ProtectedRoute>
            } />
            <Route path="/add-child" element={
              <ProtectedRoute allowedRoles={['coach', 'parent', 'admin', 'partner']}>
                <AddChild />
              </ProtectedRoute>
            } />
            <Route path="/child/:childId" element={
              <ProtectedRoute>
                <ChildProfile />
              </ProtectedRoute>
            } />
            <Route path="/child/:childId/add-measurement" element={
              <ProtectedRoute allowedRoles={['coach', 'parent', 'admin', 'athlete', 'team_manager', 'partner']}>
                <AddMeasurement />
              </ProtectedRoute>
            } />
            <Route path="/child/:childId/upload-athletic-tests" element={
              <ProtectedRoute allowedRoles={['coach', 'admin', 'partner']}>
                <UploadAthleticTests />
              </ProtectedRoute>
            } />
            <Route path="/child/:childId/add-athletic-tests" element={
              <ProtectedRoute allowedRoles={['coach', 'admin', 'partner', 'epp_staff']}>
                <AddAthleticTests />
              </ProtectedRoute>
            } />
            <Route path="/child/:childId/growth-charts" element={
              <ProtectedRoute>
                <GrowthCharts />
              </ProtectedRoute>
            } />
            <Route path="/child/:childId/add-coach-rating" element={
              <ProtectedRoute allowedRoles={['coach', 'admin', 'partner']}>
                <AddCoachRating />
              </ProtectedRoute>
            } />
            <Route path="/child/:childId/smhat-assessment" element={
              <ProtectedRoute>
                <SMHATAssessment />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
            </BadgeNotificationProvider>
        </AuthProvider>
        </div>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
